import React, { useState, useEffect } from 'react';
import { StytchB2B } from '@stytch/react/b2b';
import { AuthFlowType, B2BProducts } from '@stytch/vanilla-js/b2b';
import { useDispatch } from 'react-redux';
import { signUpModalOpened } from '../store/components';
import { Switch } from '@material-ui/core';
import translations from '../utils/translations';
import axios from 'axios';

const StytchB2BLogin = () => {
  const dispatch = useDispatch();
  const [languagePreference, setLanguagePreference] = useState(() => {
    return localStorage.getItem('language') || sessionStorage.getItem('language') || 'en';
  });
  const [showFallback, setShowFallback] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkStatus, setMagicLinkStatus] = useState(null); // null, 'sending', 'sent', 'error'
  const [fallbackError, setFallbackError] = useState(null);

  // Handle language toggle
  const handleLangToggle = (event) => {
    const newLanguage = event.target.checked ? "fr" : "en";
    setLanguagePreference(newLanguage);
    localStorage.setItem("language", newLanguage);
    sessionStorage.setItem("language", newLanguage);
  };

  // Initialize language preference and listen for changes
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || sessionStorage.getItem('language') || 'en';
    setLanguagePreference(savedLanguage);

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'language') {
        setLanguagePreference(e.newValue || 'en');
      }
    };

    // Listen for custom language change events from NavigationHeader
    const handleLanguageChange = (e) => {
      setLanguagePreference(e.detail || 'en');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Get the current window location to build the correct redirect URL
  const getRedirectUrl = () => {
    const baseUrl = window.location.origin; // This will be your dev environment URL
    return `${baseUrl}/authenticate`;
  };

  // Handle Stytch errors and show fallback
  const handleStytchError = (error) => {
    console.log('Stytch error detected:', error);

    // Check for specific errors that indicate network blocking
    const isNetworkError =
      error?.message?.includes('authorization header') ||
      error?.message?.includes('network') ||
      error?.message?.includes('CORS') ||
      error?.status === 400 ||
      error?.status === 0;

    if (isNetworkError) {
      console.log('Network restriction detected, showing fallback');
      setShowFallback(true);
      const errorMessage = languagePreference === 'fr'
        ? 'Il semble que votre réseau bloque notre service d\'authentification. Veuillez essayer la méthode de connexion alternative ci-dessous.'
        : 'It looks like your network may be blocking our authentication service. Please try the alternative sign-in method below.';
      setFallbackError(errorMessage);
    }
  };

  // Send Stytch magic link via backend
  const sendMagicLink = async () => {
    if (!magicLinkEmail || !magicLinkEmail.includes('@')) {
      const errorMessage = languagePreference === 'fr'
        ? 'Veuillez entrer une adresse courriel valide'
        : 'Please enter a valid email address';
      setFallbackError(errorMessage);
      return;
    }

    setMagicLinkStatus('sending');
    setFallbackError(null);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASICSERVICE}/api/auth/send-stytch-magic-link`, {
        email: magicLinkEmail
      });

      if (response.data.success) {
        setMagicLinkStatus('sent');
      } else {
        setMagicLinkStatus('error');
        setFallbackError('Failed to send magic link. Please try again.');
      }
    } catch (error) {
      console.error('Stytch magic link send error:', error);
      setMagicLinkStatus('error');
      const defaultErrorMessage = languagePreference === 'fr'
        ? 'Échec de l\'envoi du lien magique. Veuillez réessayer.'
        : 'Failed to send magic link. Please try again.';
      setFallbackError(error.response?.data?.error || defaultErrorMessage);
    }
  };
  // Dynamic custom strings based on language preference
  // Using exact Stytch string keys from node_modules/@stytch/vanilla-js/messages/
  const customStrings = languagePreference === 'fr' ? {
    // Form fields and labels
    'formField.email.label': 'Courriel',
    'formField.email.placeholder': 'exemple@courriel.com',

    // Main buttons
    'button.continueWithEmail': 'Continuer avec courriel',
    'button.retry': 'Réessayer',
    'button.continue': 'Continuer',

    // OAuth/SSO buttons
    'provider.continueWithSSO': 'Continuer avec SSO',
    'oauth.continueWithGoogle': 'Continuer avec Google',
    'oauth.continueWithMicrosoft': 'Continuer avec Microsoft',
    'oauth.continueWithGitHub': 'Continuer avec GitHub',
    'oauth.continueWithSlack': 'Continuer avec Slack',
    'oauth.continueWithHubSpot': 'Continuer avec HubSpot',

    // Email confirmation screen
    'emailConfirmation.title': 'Vérifiez votre courriel',
    'emailConfirmation.content': 'Un courriel a été envoyé à <bold>{email}</bold>.',

    // Email provider links
    'link.openInGmail': 'Ouvrir dans Gmail',
    'link.openInYahoo': 'Ouvrir dans Yahoo',
    'link.openInOutlook': 'Ouvrir dans Outlook',

    // General UI elements
    'methodDivider.text': 'ou',

    // SSO Discovery
    'ssoDiscovery.email.title': 'Entrez votre courriel pour continuer',
    'ssoDiscovery.title': 'Sélectionnez une connexion pour continuer',
    'ssoDiscovery.fallback.title': 'Désolé, nous n\'avons trouvé aucune connexion.',
    'ssoDiscovery.fallback.button.reset': 'Essayer une autre méthode de connexion',

    // Error messages
    'error.title': 'Il semble qu\'il y ait eu une erreur !',
    'error.default': 'Quelque chose s\'est mal passé. Réessayez plus tard ou contactez votre administrateur pour obtenir de l\'aide.',
    'error.invalidEmail': 'Le format du courriel est invalide.',
    'error.emailNotFound': 'Courriel introuvable.',
    'error.invalidCode': 'Le format du code est invalide, veuillez réessayer.',
    'error.unableToAuthMagicLink': 'Le lien magique n\'a pas pu être authentifié car il a déjà été utilisé ou a expiré. Veuillez réessayer.',
    'error.oauthFlowCallbackError': 'Une erreur s\'est produite dans le rappel du flux OAuth. Veuillez réessayer.',

    // Additional useful strings
    'emailMethodSelection.title': 'Sélectionnez comment vous souhaitez continuer.',
    'emailVerification.title': 'Vérifiez votre courriel',
    'emailVerification.content': 'Confirmez votre adresse courriel avec l\'une des options suivantes :',
    'login.title': 'Connexion / Inscription'
  } : {
    // English defaults (empty object means use Stytch defaults)

  };
const config = {
    products: [B2BProducts.emailMagicLinks, B2BProducts.sso],
    authFlowType: AuthFlowType.Discovery,
    sessionOptions: {
      sessionDurationMinutes: 60,
    },
    discoveryRedirectURL: getRedirectUrl(),
    locale: languagePreference || 'en',
    ssoOptions: {
      loginRedirectURL: getRedirectUrl(),
      signupRedirectURL: getRedirectUrl(),
      locale: languagePreference || 'en',
    },

    emailMagicLinksOptions: {
      discoveryRedirectURL:  getRedirectUrl(),
      locale: languagePreference || 'en',
    },
    callbacks: {
      onError: handleStytchError,
    }
  };

  const style = {
    fontFamily: '"GibsonRegular", sans-serif',
    container: {
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto', // Center the container
    },
    buttons: {
      primary: {
        backgroundColor: '#007bff',
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '16px',
        fontWeight: '500',
      },
      secondary: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '16px',
        fontWeight: '500',
        border: '1px solid #ddd',
        backgroundColor: 'white',
      },
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #ddd',
      fontSize: '16px',
      marginBottom: '16px',
    },
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minHeight: '400px', // Adjust as needed
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '20px',
      }}>
        {!showFallback ? (
          <>
            <StytchB2B config={config} styles={style} strings={customStrings} />
            {fallbackError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '16px',
                fontSize: '14px',
                border: '1px solid #f5c6cb'
              }}>
                {fallbackError}
              </div>
            )}
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <button
                onClick={() => setShowFallback(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {languagePreference === 'fr' ? 'Des problèmes? Essayez la connexion alternative' : 'Having trouble? Try alternative sign-in'}
              </button>
            </div>
          </>
        ) : (
          <div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '500' }}>
              {languagePreference === 'fr' ? 'Connexion alternative' : 'Alternative Sign In'}
            </h3>

            {fallbackError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                border: '1px solid #f5c6cb'
              }}>
                {fallbackError}
              </div>
            )}

            {magicLinkStatus === 'sent' ? (
              <div style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                border: '1px solid #c3e6cb'
              }}>
                <strong>{languagePreference === 'fr' ? 'Vérifiez votre courriel !' : 'Check your email!'}</strong>
                <br />
                {languagePreference === 'fr'
                  ? `Nous avons envoyé un lien de connexion sécurisé de Stytch à ${magicLinkEmail}. Cliquez sur le lien dans votre courriel pour continuer.`
                  : `We've sent a secure sign-in link from Stytch to ${magicLinkEmail}. Click the link in your email to continue.`
                }
              </div>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  {languagePreference === 'fr' ? 'Entrez votre adresse courriel et nous vous enverrons un lien de connexion sécurisé.' : 'Enter your email address and we\'ll send you a secure sign-in link.'}
                </p>

                <input
                  type="email"
                  placeholder={languagePreference === 'fr' ? 'Adresse courriel' : 'Email address'}
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    marginBottom: '16px',
                    boxSizing: 'border-box'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      sendMagicLink();
                    }
                  }}
                />

                <button
                  onClick={sendMagicLink}
                  disabled={magicLinkStatus === 'sending'}
                  style={{
                    backgroundColor: magicLinkStatus === 'sending' ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: magicLinkStatus === 'sending' ? 'not-allowed' : 'pointer',
                    width: '100%',
                    marginBottom: '16px'
                  }}
                >
                  {magicLinkStatus === 'sending'
                    ? (languagePreference === 'fr' ? 'Envoi en cours...' : 'Sending...')
                    : (languagePreference === 'fr' ? 'Envoyer le lien de connexion' : 'Send Sign-In Link')
                  }
                </button>
              </>
            )}

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => {
                  setShowFallback(false);
                  setFallbackError(null);
                  setMagicLinkStatus(null);
                  setMagicLinkEmail('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {languagePreference === 'fr' ? '← Retour à la connexion standard' : '← Back to standard sign-in'}
              </button>
            </div>
          </div>
        )}

        <span style={{
            fontSize: '14px',
            color: '#666',
            display: 'block',
            textAlign: 'center',
            marginTop: '16px'
          }}>
            {languagePreference === 'fr' ? 'Vous n\'avez pas de compte ?' : 'Don\'t have an account?'}{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                dispatch(signUpModalOpened());
              }}
              style={{
                color: '#007bff',
                textDecoration: 'none',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              {languagePreference === 'fr' ? 'S\'inscrire' : 'Sign up'}
            </a>
          </span>
      </div>
    </div>
  );
};

export default StytchB2BLogin;